import React from "react";
import PageTitle from "@Components/PageTitle/PageTitle";
import themeCreator from "@Themes/themeCreator";
import {
  Container,
  Typography,
  Box,
  List,
  ListItemText,
  ListItemIcon,
  Grid,
  Paper,
  CircularProgress,
  ListItemButton,
  IconButton,
  Chip,
} from "@mui/material";

import PDFViewer from "@Components/PDFViewer/PDFViewer";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Tooltip } from "@mui/material";

import { ListingPageContainer } from "../../ListingPageTemplate/ListingPageTemplateStyles";
import { useEffect, useState } from "react";
import useGetAll from "@Hooks/useGetAll";
import {
  CLEARANCE_AGENT_GET_ALL_DOCUMENTS_URL,
  CLEARANCE_AGENT_DELETE_DOCUMENT_URL,
  CLEARANCE_AGENT_UPDATE_DOCUMENT_URL,
} from "@API/APIRoutes";
import useDeleteItem from "@Hooks/useDeleteItem";
import useSendPutRequest from "@Hooks/useSendPutRequest";
import { AgentDocumentPopup } from "../AgentDocumentPopup/AgentDocumentPopup";
import axios from "axios";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { useParams, useLocation } from "react-router-dom";
import { DeletePopup } from "../../../Components/Popup/DeletePopup";
import { decryptId } from "../../../Utils/SecurityFunctions";

export default function AgentPortfolio() {
  const theme = themeCreator();
  const agentId = decryptId(useParams().agentId);
  const location = useLocation();
  const { agentName } = location.state || {};
  const [documents, setDocuments] = useState([]);
  const { loading, getAll } = useGetAll();
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const { loading: deleteLoading, deleteItem } = useDeleteItem();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentToEdit, setDocumentToEdit] = useState(null);
  const [showDocumentPopup, setShowDocumentPopup] = useState(false);
  const { requestMethod: updateDocument, isLoading: updateLoading } =
    useSendPutRequest(true);

  const closeDeletePopup = () => {
    setShowDeletePopup(false);
  };
  const deleteCallBack = () => {
    setDocuments((prevDocuments) =>
      prevDocuments?.filter((doc) => doc?.agentDocumentId !== selectedDocumentId)
    );
    setSelectedDocumentId(null);
    setShowDeletePopup(false);
  };
  const confirmDelete = (documentId) => {
    deleteItem(
      documentId,
      deleteCallBack,
      CLEARANCE_AGENT_DELETE_DOCUMENT_URL,
      "Document deleted successfully",
      "Error deleting document"
    );
  };
  const handleDocumentClick = async (doc) => {
    setSelectedDocument(doc);
  };

  const handleEditDocument = (doc, event) => {
    event.stopPropagation();
    setDocumentToEdit(doc);
    setShowDocumentPopup(true);
  };

  const confirmUpdateDocument = async (document, file) => {
    const response = await updateDocument(
      CLEARANCE_AGENT_UPDATE_DOCUMENT_URL(documentToEdit.agentDocumentId),
      { ...document, agentId, fileAttached: Boolean(file) },
      "Document updated successfully"
    );
    if (!response) return false;
    if (file && response?.data?.preSignedUrl) {
      await axios.put(response.data.preSignedUrl, file, {
        headers: { "Content-Type": "application/pdf" },
      });
    }
    const refreshedDocuments = await getAll(
      `${CLEARANCE_AGENT_GET_ALL_DOCUMENTS_URL}/${agentId}`
    );
    const formattedDocuments = refreshedDocuments?.map((doc) => ({
      ...doc,
      emails: Array.isArray(doc?.emails) ? doc.emails : [],
      documentName: doc?.documentName.replace(/_/g, " "),
    }));
    setDocuments(formattedDocuments || []);
    const refreshedSelectedDocument = formattedDocuments?.find(
      (doc) => doc.agentDocumentId === documentToEdit.agentDocumentId
    );
    if (refreshedSelectedDocument) setSelectedDocument(refreshedSelectedDocument);
    setShowDocumentPopup(false);
    setDocumentToEdit(null);
    return true;
  };

  const handleDeleteDocument = (docId, event) => {
    // Prevent the click from propagating to the parent ListItemButton
    event.stopPropagation();
    // Set the selected document ID to the one being deleted
    setSelectedDocumentId(docId);
    setShowDeletePopup(true);
  };

  useEffect(() => {
    getAll(`${CLEARANCE_AGENT_GET_ALL_DOCUMENTS_URL}/${agentId}`).then(
      (response) => {
        // format the response to remove _ from documentName
        const formattedResponse = response?.map((doc) => {
          return {
            ...doc,
            emails: Array.isArray(doc?.emails) ? doc.emails : [],
            documentName: doc?.documentName.replace(/_/g, " "),
          };
        });
        setDocuments(formattedResponse);
      }
    );
  }, [agentId]);
  return (
    <ListingPageContainer className="listingPageContainer">
      <Container
        sx={{ pb: 5, pr: "0px !important", pl: "0px !important" }}
        disableGutters
      >
        <Box className="LabelButtonBox">
          <PageTitle
            title="Agent Portfolio"
            PageIcon={<AccountBoxIcon color="white" fontSize="large" />}
            iconColor={theme.palette.primary.main}
          />

          <Typography
            variant="h5"
            sx={{
              color: theme.palette.primary.dark,
              ml: 5, // Align with the title
              mt: 0.5,
              fontWeight: 500,
              fontStyle: "italic",
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              paddingLeft: 2,
            }}
          >
            {agentName || "Unknown Agent"}
          </Typography>
        </Box>
        {/* Document List and PDF Viewer Section */}
        <Box sx={{ mt: 4 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : documents?.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
              No documents found for this agent.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {/* Document List - Takes 1/3 of the width */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    height: "100%",
                    maxHeight: "70vh",
                    overflow: "auto",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      borderBottom: `2px solid ${theme.palette.primary.main}`,
                      pb: 1,
                    }}
                  >
                    Agent Documents
                  </Typography>

                  <List>
                    {documents?.map((doc) => (
                      <ListItemButton
                        key={doc?.agentDocumentId}
                        onClick={() => handleDocumentClick(doc)}
                        selected={selectedDocument?.agentDocumentId === doc?.agentDocumentId}
                        sx={{
                          mb: 1,
                          borderRadius: 1,
                          transition: "all 0.2s ease",
                          position: "relative",
                          "&.Mui-selected": {
                            backgroundColor: `${theme.palette.primary.main}20`,
                            borderLeft: `4px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 5px ${theme.palette.primary.main}30`,
                          },
                          "&:hover": {
                            backgroundColor: `${theme.palette.primary.light}10`,
                          },
                        }}
                      >
                        <ListItemIcon>
                          <DescriptionIcon
                            color={
                              selectedDocument?.agentDocumentId === doc?.agentDocumentId
                                ? "primary"
                                : "inherit"
                            }
                            sx={{
                              transform:
                                selectedDocument?.agentDocumentId === doc?.agentDocumentId
                                  ? "scale(1.2)"
                                  : "scale(1)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            doc?.documentName || doc?.name || "Unnamed Document"
                          }
                          primaryTypographyProps={{
                            fontWeight:
                              selectedDocument?.agentDocumentId === doc?.agentDocumentId
                                ? "bold"
                                : "normal",
                            color:
                              selectedDocument?.agentDocumentId === doc?.agentDocumentId
                                ? theme.palette.primary.main
                                : "inherit",
                          }}
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              {/* Date range indicator */}
                              {(doc?.startingDate || doc?.expiryDate) && (
                                <Box component="span">
                                  {doc?.startingDate
                                    ? new Date(
                                        doc?.startingDate
                                      ).toLocaleDateString()
                                    : "N/A"}
                                  {" - "}
                                  {doc?.expiryDate
                                    ? new Date(
                                        doc?.expiryDate
                                      ).toLocaleDateString()
                                    : "Present"}
                                </Box>
                              )}
                              {doc?.emails?.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {doc.emails.map((email) => (
                                    <Chip
                                      key={email}
                                      label={email}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          }
                        />

                        <Tooltip title="Edit document">
                          <IconButton
                            onClick={(e) => handleEditDocument(doc, e)}
                            size="small"
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Simple Delete Button */}
                        <IconButton
                          onClick={(e) =>
                            handleDeleteDocument(doc?.agentDocumentId, e)
                          }
                          size="small"
                          sx={{
                            color: theme.palette.error.main,
                            mr: 0.5,
                            ml: "auto",
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* PDF Viewer - Takes 2/3 of the width */}
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    height: "70vh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: selectedDocument ? "flex-start" : "center",
                    alignItems: selectedDocument ? "stretch" : "center",
                  }}
                >
                  {!selectedDocument ? (
                    <Typography variant="body1" color="text.secondary">
                      Select a document to view its contents
                    </Typography>
                  ) : false ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        flexGrow: 1,
                        height: "calc(100% - 80px)",
                        overflow: "hidden",
                      }}
                    >
                      <PDFViewer
                        fileUrl={selectedDocument?.presignedUrl}
                        style={{ height: "100%", width: "100%" }} // Add explicit styling to PDFViewer
                      />{" "}
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>
      <AgentDocumentPopup
        showPopup={showDocumentPopup}
        closePopup={() => {
          setShowDocumentPopup(false);
          setDocumentToEdit(null);
        }}
        Confirm={confirmUpdateDocument}
        loading={updateLoading}
        initialDocument={documentToEdit}
        isEditing
      />
      {DeletePopup(
        "Document",
        showDeletePopup,
        closeDeletePopup,
        confirmDelete,
        selectedDocumentId,
        deleteLoading
      )}
    </ListingPageContainer>
  );
}
